import PropTypes from "prop-types";
import { memo, useRef } from "react";
import Merchandising from "app/pages/Listing/merchandising/Merchandising";
import { RESOLUTION } from "app/constants";
import { resolutionPropType } from "app/utils/propTypes";
import "./zoom-merchandising-carousel.scss";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import SimpleBackgroundProgressiveImg from "app/pages/.shared/SimpleBackgroundProgressiveImg";
import SimpleProgressiveImg from "app/pages/.shared/SimpleProgressiveImg";
import Loader from "app/pages/.shared/Loader";
import { ArrowLeftIcon, ArrowRightIcon } from "app/icons/navigation";

const IMG_SIZES_LARGE = {
	large: { width: 1024, height: 415 },
	xlarge: { width: 1024, height: 415 },
	medium: { width: 1024, height: 415 },
};

const IMG_SIZES_SMALL = {
	small: { width: 768, height: 834 },
};

const CLOUDINARY_OPTIONS = ["c_crop"];

const spaceBetween = {
	[RESOLUTION.XLARGE]: 22,
	[RESOLUTION.LARGE]: 22,
	[RESOLUTION.MEDIUM]: 15,
	[RESOLUTION.SMALL]: 15,
};

// eslint-disable-next-line react/no-multi-comp
const ZoomMerchandisingCarousel = ({ merchandisingList, resolution }) => {
	const prevRef = useRef(null);
	const nextRef = useRef(null);

	const handleSwiper = swiper => {
		swiper.params.navigation.prevEl = prevRef.current;
		swiper.params.navigation.nextEl = nextRef.current;
		swiper.navigation.init();
		swiper.navigation.update();
	};

	const carouselContainerStyle = {
		minHeight: "auto",
		height: "auto",
	};

	if (resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE) {
		carouselContainerStyle.height = "350px";
	} else if (resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL) {
		carouselContainerStyle.minHeight = "264px";
	}

	const isServerSide = typeof window === "undefined";

	return isServerSide ? (
		<div
			style={{
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				...carouselContainerStyle,
			}}
		>
			<Loader />
		</div>
	) : (
		<div style={carouselContainerStyle}>
			<div
				ref={prevRef}
				className="zoom-merchandising-carousel__button zoom-merchandising-carousel__button--previous"
			>
				<ArrowLeftIcon className="icon icon--previous" />
			</div>
			<div
				ref={nextRef}
				className="zoom-merchandising-carousel__button zoom-merchandising-carousel__button--next"
			>
				<ArrowRightIcon className="icon icon--next" />
			</div>
			<Swiper
				className="zoom-merchandising-carousel"
				modules={[Navigation]}
				navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
				onSwiper={handleSwiper}
				speed={500}
				slidesPerView={1.18}
				roundLengths={true}
				centeredSlides={true}
				loop={true}
				spaceBetween={spaceBetween[resolution]}
			>
				{merchandisingList.map(merchandising => {
					let imageElement = false;

					if (resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE) {
						imageElement = (
							<SimpleBackgroundProgressiveImg
								resolution={resolution}
								sizes={IMG_SIZES_LARGE}
								cloudinaryOptions={CLOUDINARY_OPTIONS}
								src={get(merchandising, "photoLarge.url")}
								backgroundPosition="center"
							/>
						);
					} else if (resolution === RESOLUTION.MEDIUM) {
						imageElement = (
							<SimpleProgressiveImg
								resolution={resolution}
								sizes={IMG_SIZES_LARGE}
								cloudinaryOptions={CLOUDINARY_OPTIONS}
								src={get(merchandising, "photoLarge.url")}
							/>
						);
					} else if (resolution === RESOLUTION.SMALL) {
						imageElement = (
							<SimpleProgressiveImg
								sizes={IMG_SIZES_SMALL}
								cloudinaryOptions={CLOUDINARY_OPTIONS}
								src={get(merchandising, "photoSmall.url")}
							/>
						);
					}

					const merchKey = `${merchandising.code}-${merchandising.startAt}`;

					return (
						<SwiperSlide key={merchKey}>
							<Merchandising
								imageElement={imageElement}
								ctaButtonText={get(merchandising, "button.label")}
								ctaButtonBackgroundColor={get(
									merchandising,
									"button.backgroundColor"
								)}
								ctaButtonColor={get(merchandising, "button.color")}
								merchandisingCode={get(merchandising, "code")}
								position={get(merchandising, "position")}
								indexCarousel={merchandising.headerIndex}
								endAt={merchandising.endAt}
								countdownPosition={get(merchandising, "countdown.position")}
								countdownTextColor={get(merchandising, "countdown.textColor")}
								countdownStartAt={get(merchandising, "countdown.startAt")}
								countdownEndAt={get(merchandising, "countdown.endAt")}
								daysInHours={get(merchandising, "countdown.daysInHours")}
								redirectUrl={get(merchandising, "redirectUrl")}
							/>
						</SwiperSlide>
					);
				})}
			</Swiper>
		</div>
	);
};

ZoomMerchandisingCarousel.defaultProps = {
	merchandisingList: [],
};

ZoomMerchandisingCarousel.propTypes = {
	merchandisingList: PropTypes.arrayOf(PropTypes.object),
	resolution: resolutionPropType,
};

export default memo(ZoomMerchandisingCarousel, (prevProps, nextProps) => {
	return (
		isEqual(prevProps.merchandisingList, nextProps.merchandisingList) &&
		prevProps.resolution === nextProps.resolution
	);
});
